import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Download,
  Calendar,
  Users,
  Clock,
  TrendingUp,
  Filter,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const HRReportsPage = () => {
  const [loading, setLoading] = useState(false);
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [payrollReport, setPayrollReport] = useState(null);
  
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  // Generate month options
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleString('default', { month: 'long', year: 'numeric' });
    monthOptions.push({ value, label });
  }

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [attendanceRes, payrollRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/hr/reports/monthly-attendance?month=${selectedMonth}`, { headers }),
        axios.get(`${API_BASE_URL}/hr/reports/payroll-summary?month=${selectedMonth}`, { headers })
      ]);
      
      setAttendanceReport(attendanceRes.data);
      setPayrollReport(payrollRes.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const exportAttendanceToExcel = () => {
    if (!attendanceReport?.data) return;
    
    const ws = XLSX.utils.json_to_sheet(attendanceReport.data.map(row => ({
      'Employee Code': row.employee_code,
      'Employee Name': row.employee_name,
      'Department': row.department,
      'Present Days': row.present_days,
      'Late Days': row.late_days,
      'Half Days': row.half_days,
      'Leave Days': row.leave_days,
      'Absent Days': row.absent_days
    })));
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance Report');
    XLSX.writeFile(wb, `Attendance_Report_${selectedMonth}.xlsx`);
    toast.success('Report exported');
  };

  const exportPayrollToExcel = () => {
    if (!payrollReport?.slips) return;
    
    const ws = XLSX.utils.json_to_sheet(payrollReport.slips.map(slip => ({
      'Slip Number': slip.slip_number,
      'Employee Name': slip.user_name,
      'Base Salary': slip.base_salary,
      'Allowances': slip.housing_allowance + slip.transport_allowance + slip.food_allowance + slip.other_allowances,
      'Overtime': slip.overtime_pay,
      'Incentives': slip.incentives,
      'Total Earnings': slip.total_earnings,
      'Penalties': slip.penalties,
      'Leave Deductions': slip.leave_deductions,
      'Advance Deduction': slip.advance_deduction,
      'Total Deductions': slip.total_deductions,
      'Net Salary': slip.net_salary
    })));
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payroll Report');
    XLSX.writeFile(wb, `Payroll_Report_${selectedMonth}.xlsx`);
    toast.success('Report exported');
  };

  // Calculate attendance summary
  const attendanceSummary = attendanceReport?.data?.reduce((acc, row) => {
    acc.totalPresent += row.present_days;
    acc.totalLate += row.late_days;
    acc.totalAbsent += row.absent_days;
    acc.totalLeave += row.leave_days;
    return acc;
  }, { totalPresent: 0, totalLate: 0, totalAbsent: 0, totalLeave: 0 }) || {};

  return (
    <div className="space-y-6" data-testid="hr-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">HR Reports</h1>
          <p className="text-wt-text-muted">Monthly attendance and payroll reports</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-48 bg-wt-surface border-wt-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-wt-surface border-wt-border">
              {monthOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
        </div>
      ) : (
        <Tabs defaultValue="attendance" className="w-full">
          <TabsList className="bg-wt-surface border border-wt-border">
            <TabsTrigger value="attendance" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
              <Clock className="h-4 w-4 mr-2" />
              Attendance Report
            </TabsTrigger>
            <TabsTrigger value="payroll" className="data-[state=active]:bg-wt-gold/20 data-[state=active]:text-wt-gold">
              <TrendingUp className="h-4 w-4 mr-2" />
              Payroll Report
            </TabsTrigger>
          </TabsList>

          {/* Attendance Report Tab */}
          <TabsContent value="attendance" className="mt-4 space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-wt-surface border-wt-border">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">{attendanceSummary.totalPresent || 0}</p>
                  <p className="text-sm text-wt-text-muted">Total Present Days</p>
                </CardContent>
              </Card>
              <Card className="bg-wt-surface border-wt-border">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-400">{attendanceSummary.totalLate || 0}</p>
                  <p className="text-sm text-wt-text-muted">Total Late Days</p>
                </CardContent>
              </Card>
              <Card className="bg-wt-surface border-wt-border">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-400">{attendanceSummary.totalLeave || 0}</p>
                  <p className="text-sm text-wt-text-muted">Total Leave Days</p>
                </CardContent>
              </Card>
              <Card className="bg-wt-surface border-wt-border">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-400">{attendanceSummary.totalAbsent || 0}</p>
                  <p className="text-sm text-wt-text-muted">Total Absent Days</p>
                </CardContent>
              </Card>
            </div>

            {/* Report Table */}
            <Card className="bg-wt-surface border-wt-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-wt-text-primary">
                  Monthly Attendance - {new Date(selectedMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}
                </CardTitle>
                <Button
                  onClick={exportAttendanceToExcel}
                  className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                  disabled={!attendanceReport?.data?.length}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Excel
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-wt-bg border-b border-wt-border">
                      <tr>
                        <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Code</th>
                        <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Employee</th>
                        <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Department</th>
                        <th className="text-center p-4 text-xs font-medium text-green-400">Present</th>
                        <th className="text-center p-4 text-xs font-medium text-yellow-400">Late</th>
                        <th className="text-center p-4 text-xs font-medium text-orange-400">Half</th>
                        <th className="text-center p-4 text-xs font-medium text-blue-400">Leave</th>
                        <th className="text-center p-4 text-xs font-medium text-red-400">Absent</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-wt-border">
                      {attendanceReport?.data?.map((row, idx) => (
                        <tr key={idx} className="hover:bg-wt-bg/50">
                          <td className="p-4 text-sm text-wt-text-muted font-mono">{row.employee_code}</td>
                          <td className="p-4 text-sm text-wt-text-primary">{row.employee_name}</td>
                          <td className="p-4 text-sm text-wt-text-muted">{row.department}</td>
                          <td className="p-4 text-sm text-center text-green-400 font-medium">{row.present_days}</td>
                          <td className="p-4 text-sm text-center text-yellow-400 font-medium">{row.late_days}</td>
                          <td className="p-4 text-sm text-center text-orange-400 font-medium">{row.half_days}</td>
                          <td className="p-4 text-sm text-center text-blue-400 font-medium">{row.leave_days}</td>
                          <td className="p-4 text-sm text-center text-red-400 font-medium">{row.absent_days}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!attendanceReport?.data || attendanceReport.data.length === 0) && (
                    <div className="text-center py-12 text-wt-text-muted">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No attendance data for this month</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payroll Report Tab */}
          <TabsContent value="payroll" className="mt-4 space-y-4">
            {/* Summary Cards */}
            {payrollReport && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-wt-surface border-wt-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-wt-gold">{payrollReport.employee_count}</p>
                    <p className="text-sm text-wt-text-muted">Employees</p>
                  </CardContent>
                </Card>
                <Card className="bg-wt-surface border-wt-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-400">
                      {payrollReport.total_earnings?.toLocaleString()}
                    </p>
                    <p className="text-sm text-wt-text-muted">Total Earnings (AED)</p>
                  </CardContent>
                </Card>
                <Card className="bg-wt-surface border-wt-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-red-400">
                      {payrollReport.total_deductions?.toLocaleString()}
                    </p>
                    <p className="text-sm text-wt-text-muted">Total Deductions (AED)</p>
                  </CardContent>
                </Card>
                <Card className="bg-wt-surface border-wt-border">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-400">
                      {payrollReport.total_net_salary?.toLocaleString()}
                    </p>
                    <p className="text-sm text-wt-text-muted">Net Payroll (AED)</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Report Table */}
            <Card className="bg-wt-surface border-wt-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-wt-text-primary">
                  Payroll Summary - {new Date(selectedMonth + '-01').toLocaleString('default', { month: 'long', year: 'numeric' })}
                </CardTitle>
                <Button
                  onClick={exportPayrollToExcel}
                  className="bg-wt-gold hover:bg-wt-gold-hover text-black"
                  disabled={!payrollReport?.slips?.length}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Excel
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-wt-bg border-b border-wt-border">
                      <tr>
                        <th className="text-left p-4 text-xs font-medium text-wt-text-muted">Employee</th>
                        <th className="text-right p-4 text-xs font-medium text-wt-text-muted">Base Salary</th>
                        <th className="text-right p-4 text-xs font-medium text-wt-text-muted">Allowances</th>
                        <th className="text-right p-4 text-xs font-medium text-green-400">Earnings</th>
                        <th className="text-right p-4 text-xs font-medium text-red-400">Deductions</th>
                        <th className="text-right p-4 text-xs font-medium text-wt-gold">Net Salary</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-wt-border">
                      {payrollReport?.slips?.map((slip) => (
                        <tr key={slip.id} className="hover:bg-wt-bg/50">
                          <td className="p-4">
                            <p className="text-sm text-wt-text-primary">{slip.user_name}</p>
                            <p className="text-xs text-wt-text-muted">{slip.slip_number}</p>
                          </td>
                          <td className="p-4 text-sm text-right text-wt-text-primary">
                            {slip.base_salary.toLocaleString()}
                          </td>
                          <td className="p-4 text-sm text-right text-wt-text-muted">
                            {(slip.housing_allowance + slip.transport_allowance + slip.food_allowance + slip.other_allowances).toLocaleString()}
                          </td>
                          <td className="p-4 text-sm text-right text-green-400 font-medium">
                            {slip.total_earnings.toLocaleString()}
                          </td>
                          <td className="p-4 text-sm text-right text-red-400 font-medium">
                            {slip.total_deductions.toLocaleString()}
                          </td>
                          <td className="p-4 text-sm text-right text-wt-gold font-bold">
                            {slip.net_salary.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {payrollReport?.slips?.length > 0 && (
                      <tfoot className="bg-wt-bg border-t-2 border-wt-gold">
                        <tr>
                          <td className="p-4 font-bold text-wt-text-primary">TOTAL</td>
                          <td className="p-4"></td>
                          <td className="p-4"></td>
                          <td className="p-4 text-right text-green-400 font-bold">
                            {payrollReport.total_earnings?.toLocaleString()}
                          </td>
                          <td className="p-4 text-right text-red-400 font-bold">
                            {payrollReport.total_deductions?.toLocaleString()}
                          </td>
                          <td className="p-4 text-right text-wt-gold font-bold">
                            {payrollReport.total_net_salary?.toLocaleString()}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                  {(!payrollReport?.slips || payrollReport.slips.length === 0) && (
                    <div className="text-center py-12 text-wt-text-muted">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No payroll data for this month</p>
                      <p className="text-xs mt-1">Process payroll to generate data</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default HRReportsPage;
